/**
 * Single Supabase client instance for the whole app. Every other module
 * that talks to Supabase (api/*.ts, auth/*.ts) imports this — never creates
 * its own client. That keeps auth state and connection pooling consistent.
 *
 * Environment variables are read via Vite's import.meta.env mechanism.
 * See .env.example for the variables this expects, and SETUP.md for how
 * to obtain real values from a Supabase project.
 */

import { createClient } from '@supabase/supabase-js';
import type { Database } from './database.types';

const supabaseUrl = import.meta.env.VITE_SUPABASE_URL;
const supabaseAnonKey = import.meta.env.VITE_SUPABASE_ANON_KEY;

if (!supabaseUrl || !supabaseAnonKey) {
  // Fails loudly and early rather than letting every downstream query fail
  // with a confusing network error. If you're seeing this during local
  // development, copy .env.example to .env and fill in real values — see
  // SETUP.md for how to create a Supabase project and find these.
  throw new Error(
    'Missing Supabase environment variables. Copy .env.example to .env and ' +
      'set VITE_SUPABASE_URL and VITE_SUPABASE_ANON_KEY. See SETUP.md.'
  );
}

export const supabase = createClient<Database>(supabaseUrl, supabaseAnonKey, {
  auth: {
    persistSession: true,
    autoRefreshToken: true,
    // Explicit, not just relying on the library default (which is also
    // true) — this is the setting that makes password-recovery and
    // magic-link tokens in the URL actually get picked up and exchanged
    // for a session automatically on page load. See LoginPage.tsx and
    // ResetPasswordPage.tsx for the full password-reset flow this
    // enables; an earlier version of this app had no page actually
    // using the session this produces, which was the real bug behind a
    // reported "password reset doesn't work" issue.
    detectSessionInUrl: true,
  },
});
